import { ResourceClient, type HttpClient } from "@dl/web-core-js/api";
import type { Currency, ReplenishmentPolicy, SupplierPaymentTerms, UsageSource } from "./manufacturing";

/**
 * Replenishment: what to buy, when, and what it will cost.
 *
 * The nightly run (and the Recalculate button) plans every purchased material
 * and stores one row per material — a snapshot. Everything here reads the
 * latest finished run; nothing is recomputed in the browser except what a
 * person types. Wire names match the Java `@JsonProperty`s.
 */

/** RED: below safety stock. YELLOW: at or below the order point. GREEN: fine. OVER: above the maximum. */
export const REPLENISHMENT_ZONES = ["RED", "YELLOW", "GREEN", "OVER"] as const;
export type ReplenishmentZone = (typeof REPLENISHMENT_ZONES)[number];

export type LeadTimeSource = "PART_SUPPLIER" | "SUPPLIER_MEASURED" | "SUPPLIER_FIXED" | "DEFAULT";

/** One material as the latest run saw it. */
export interface ReplenishmentLineDTO {
  id: number;
  replenishment_run_id: number;
  part_id: number;
  part_number: string;
  part_name: string;
  part_group_code: string | null;
  stock_unit_code: string | null;
  warehouse_id: number;
  warehouse_code: string;
  supplier_id: number | null;
  supplier_code: string | null;
  supplier_name: string | null;
  /** What it is ordered in: a PER_PART unit (whole ones) or the SHARED unit it travels in. */
  order_unit_id: number | null;
  order_unit_code: string | null;
  order_unit_name: string | null;
  order_unit_name_fr: string | null;
  order_unit_kind: OrderUnitKind | null;
  /** PER_PART: stock units one holds; the order quantity is a whole number of these. */
  stock_units_per_order_unit: number | null;
  /** SHARED: load units one stock unit takes in it. */
  load_per_stock_unit: number | null;
  replenishment_policy: ReplenishmentPolicy;
  usage_source: UsageSource;
  recorded_usage: number;
  derived_usage: number;
  average_daily_usage: number;
  is_usage_overridden: boolean;
  /** Strategic extra included. */
  safety_days: number;
  is_strategic: boolean;
  is_run_out: boolean;
  /** Effective: from the run to the arrival of an order placed that day, through the supplier's closures. */
  lead_time_days: number;
  /** As quoted (link, supplier, default), before closures. */
  quoted_lead_time_days: number | null;
  lead_time_source: LeadTimeSource;
  /** When an order placed on the run's day would arrive. */
  arrival_date: string | null;
  /** Order on or before this day to ship before the closure below. */
  order_by_date: string | null;
  /** The closure that delays an order placed now, or that one placed soon would run into. */
  closure_reason: string | null;
  closure_start_date: string | null;
  closure_end_date: string | null;
  order_cycle_days: number;
  minimum_order_quantity: number | null;
  order_multiple: number | null;
  red_quantity: number;
  yellow_quantity: number;
  green_quantity: number;
  /** The order point: red + yellow. */
  minimum_quantity: number;
  maximum_quantity: number;
  on_hand_quantity: number;
  on_order_quantity: number;
  drafted_quantity: number;
  reserved_quantity: number;
  /** What unstarted pipeline lots will need — information, never ordered from. */
  planned_quantity: number;
  /** On hand + on order − reserved. */
  position_quantity: number;
  zone: ReplenishmentZone;
  suggested_quantity: number;
  /** Suggested less what is already drafted. */
  order_quantity: number;
  unit_price: number | null;
  currency: Currency | null;
  order_amount_cad: number | null;
  days_of_cover: number | null;
  /** Position ÷ order point: under 1 is time to order. */
  buffer_level: number | null;
  note: string | null;
}

/** One shared order unit (a truck, a container) and what the latest run loads in it. */
export interface SharedUnitGauge {
  order_unit_id: number;
  code: string;
  name: string;
  name_fr: string;
  supplier_id: number;
  supplier_name: string;
  capacity: number;
  minimum_load: number;
  /** Fractions of capacity the supplier sells; empty: any load from the minimum. */
  fill_levels: number[];
  load_unit_label: string;
  order_cycle_days: number | null;
  part_count: number;
  planned_load: number;
  /** The fraction it is billed at (0.5: half a truck); null when nothing rides. */
  planned_fill: number | null;
  order_amount_cad: number;
  is_due: boolean;
  is_urgent: boolean;
}

export interface ReplenishmentSummary {
  replenishment_run_id: number | null;
  as_of_date: string | null;
  finished_at: string | null;
  material_count: number;
  red_count: number;
  to_order_count: number;
  to_order_amount_cad: number;
  /** Shared order units (trucks, containers) with a load to send. */
  shared_due_count: number;
  /** Late, stale ones left out. */
  late_order_count: number;
  /** Issued orders nobody expects any more: to be closed. */
  stale_order_count: number;
  held_alert_count: number;
  awaiting_approval_count: number;
  awaiting_approval_amount_cad: number;
  draft_count: number;
  negative_count: number;
  /** Next four weeks: received-not-yet-due plus issued orders. */
  cash_out_committed_cad: number;
  /** Next four weeks: drafts and orders awaiting approval. */
  cash_out_pending_cad: number;
  /** Next four weeks: suggestions not drafted yet. */
  cash_out_suggested_cad: number;
  can_approve: boolean;
  shared_units: SharedUnitGauge[];
}

export interface RunResult {
  replenishment_run_id: number;
  as_of_date: string;
  material_count: number;
  order_count: number;
  draft_count: number;
  duration_ms: number;
}

export interface DraftRequest {
  lines: Array<{ part_id: number; quantity: number; supplier_id: number | null; warehouse_id: number | null }>;
}

export interface DraftResult {
  orders: Array<{ purchase_order_id: number; number: number; supplier_name: string; line_count: number; total: number }>;
}

export const CASH_KINDS = ["RECEIVED", "ON_ORDER", "PENDING", "SUGGESTED"] as const;
export type CashKind = (typeof CASH_KINDS)[number];

export interface CashProjection {
  currency: "CAD";
  /** Each week's Monday; the first is this week. */
  weeks: string[];
  rows: Array<{
    supplier_id: number | null;
    supplier_name: string | null;
    payment_terms: SupplierPaymentTerms | null;
    payment_terms_days: number;
    deposit_percent: number | null;
    part_group_code: string | null;
    part_group_name: string | null;
    kind: CashKind;
    amounts: number[];
    later: number;
  }>;
  totals: Array<{ kind: CashKind; amounts: number[]; later: number }>;
}

export interface LateOrder {
  purchase_order_id: number;
  number: number;
  supplier_id: number;
  supplier_name: string;
  warehouse_code: string;
  is_on_hold: boolean;
  issued_date: string | null;
  due_date: string | null;
  days_late: number;
  late_line_count: number;
  outstanding_amount_cad: number;
  days_on_hold: number | null;
  is_hold_alert: boolean;
  /** Every open line is past the stale-order days: planning ignores it; close it. */
  is_stale: boolean;
  is_from_sigm: boolean;
}

export type NegativeFix = "OUTPUT" | "TRANSFER" | "POLICY" | "COUNT";

export interface NegativeStock {
  part_id: number;
  part_number: string;
  part_name: string;
  part_type: string;
  inventory_policy: string;
  stock_unit_code: string | null;
  warehouse_id: number;
  warehouse_code: string;
  quantity: number;
  last_movement_date: string | null;
  has_receipts: boolean;
  fix: NegativeFix;
  elsewhere: Array<{ warehouse_id: number; warehouse_code: string; quantity: number }>;
}

export interface SafetyDaysReview {
  from_date: string;
  to_date: string;
  run_count: number;
  materials: Array<{
    part_id: number;
    part_number: string;
    part_name: string;
    part_group_id: number | null;
    part_group_code: string | null;
    part_group_name: string | null;
    safety_days: number;
    part_safety_days: number | null;
    group_safety_days: number;
    strategic_extra_days: number;
    days_observed: number;
    days_out: number;
    days_red: number;
    days_at_or_below_minimum: number;
    suggested_safety_days: number;
    reason: "RAN_OUT" | "MOSTLY_RED" | "NEVER_LOW";
  }>;
}

export interface SupplierLeadTimeReview {
  supplier_id: number;
  supplier_code: string;
  supplier_name: string;
  mode: "FIXED" | "MEASURED";
  fixed_days: number | null;
  measured_days: number | null;
  receipt_count: number;
  promised_count: number | null;
  /** 0..1 */
  on_time_rate: number | null;
  effective_days: number;
  source: LeadTimeSource;
  is_drifting: boolean;
  material_count: number;
}

export interface PartReplenishmentDTO {
  id: number;
  part_id: number;
  replenishment_policy: ReplenishmentPolicy | null;
  usage_source: UsageSource | null;
  safety_days: number | null;
  fixed_minimum: number | null;
  fixed_maximum: number | null;
  average_daily_usage_override: number | null;
  average_daily_usage_override_reason: string | null;
  average_daily_usage_override_end_date: string | null;
  note: string | null;
}

export interface PartReplenishmentDetail {
  part_id: number;
  line: ReplenishmentLineDTO | null;
  settings: PartReplenishmentDTO | null;
  group_policy: ReplenishmentPolicy | null;
  group_usage_source: UsageSource | null;
  group_safety_days: number | null;
  group_strategic_extra_days: number | null;
  projection: Array<{
    week_start: string;
    demand: number;
    receipts: number;
    end_quantity: number;
    zone: ReplenishmentZone | "OUT";
  }>;
  open_lines: Array<{
    purchase_order_id: number;
    number: number;
    status: string;
    is_on_hold: boolean;
    supplier_name: string;
    warehouse_code: string;
    outstanding_quantity: number;
    due_date: string | null;
    is_late: boolean;
    /** Past the stale-order days: not counted as on order. */
    is_stale: boolean;
    supplier_id: number;
  }>;
  reservations: Array<{
    production_order_id: number;
    number: number;
    status: string;
    part_number: string;
    warehouse_code: string;
    remaining_quantity: number;
    due_date: string | null;
  }>;
  history: Array<{
    as_of_date: string;
    on_hand_quantity: number;
    position_quantity: number;
    red_quantity: number;
    minimum_quantity: number;
    maximum_quantity: number;
    zone: ReplenishmentZone;
  }>;
}

/** PER_PART: holds an amount of one part (a barrel, a roll, a box). SHARED: one supplier's parts travel together in it (a truck, a container). */
export const ORDER_UNIT_KINDS = ["PER_PART", "SHARED"] as const;
export type OrderUnitKind = (typeof ORDER_UNIT_KINDS)[number];

/** What things are ordered in. Was the truck group. */
export interface OrderUnitDTO {
  id: number;
  code: string;
  name: string;
  name_fr: string;
  kind: OrderUnitKind;
  /** SHARED only. */
  supplier_id: number | null;
  supplier_name?: string | null;
  /** SHARED only: a full one, in load_unit_label (sheets, pallets, m3). */
  capacity: number | null;
  /** SHARED only, without fill levels: below this it does not leave; parts are topped up to reach it. */
  minimum_load: number | null;
  load_unit_label: string | null;
  /** SHARED only: fractions of capacity the supplier sells, e.g. [0.25, 0.5, 1]. Empty: any load from the minimum. */
  fill_levels: number[];
  order_cycle_days: number | null;
  is_active: boolean;
  note: string | null;
  part_count?: number;
}

/** A subcontracted part's bill: what the subcontractor uses of ours to make one. */
export interface SubcontractComponentDTO {
  id: number;
  part_id: number;
  component_part_id: number;
  component_part_number?: string | null;
  component_part_name?: string | null;
  component_unit_code?: string | null;
  quantity: number;
  note: string | null;
}

export class ReplenishmentClient extends ResourceClient<ReplenishmentLineDTO> {
  constructor(http: HttpClient) {
    super(http, "/purchasing/replenishment");
  }

  run(): Promise<RunResult> {
    return this.http.post<RunResult>(`${this.path}/run`, {});
  }

  summary(): Promise<ReplenishmentSummary> {
    return this.http.get<ReplenishmentSummary>(`${this.path}/summary`);
  }

  drafts(request: DraftRequest): Promise<DraftResult> {
    return this.http.post<DraftResult>(`${this.path}/drafts`, request);
  }

  /** One material at one of its stocking points; no warehouse: its consumption point. */
  part(partId: number, warehouseId?: number | null): Promise<PartReplenishmentDetail> {
    const at = warehouseId == null ? "" : `?warehouse_id=${String(warehouseId)}`;
    return this.http.get<PartReplenishmentDetail>(`${this.path}/part/${String(partId)}${at}`);
  }

  cash(): Promise<CashProjection> {
    return this.http.get<CashProjection>(`${this.path}/cash`);
  }

  late(): Promise<LateOrder[]> {
    return this.http.get<LateOrder[]>(`${this.path}/late`);
  }

  negatives(): Promise<NegativeStock[]> {
    return this.http.get<NegativeStock[]>(`${this.path}/negatives`);
  }

  safetyDaysReview(): Promise<SafetyDaysReview> {
    return this.http.get<SafetyDaysReview>(`${this.path}/review/safety-days`);
  }

  leadTimeReview(): Promise<SupplierLeadTimeReview[]> {
    return this.http.get<SupplierLeadTimeReview[]>(`${this.path}/review/lead-times`);
  }
}

export class OrderUnitClient extends ResourceClient<OrderUnitDTO> {
  constructor(http: HttpClient) {
    super(http, "/purchasing/order-unit");
  }
}

export class SubcontractComponentClient extends ResourceClient<SubcontractComponentDTO> {
  constructor(http: HttpClient) {
    super(http, "/purchasing/subcontract-component");
  }
}

export class PartReplenishmentClient extends ResourceClient<PartReplenishmentDTO> {
  constructor(http: HttpClient) {
    super(http, "/purchasing/part-replenishment");
  }
}

/** Shared query keys, so a draft created anywhere refreshes every replenishment view. */
export const replenishmentKeys = {
  all: ["replenishment"] as const,
  summary: ["replenishment", "summary"] as const,
  lines: ["replenishment", "lines"] as const,
  part: (partId: number, warehouseId?: number | null) => ["replenishment", "part", partId, warehouseId ?? null] as const,
  cash: ["replenishment", "cash"] as const,
  late: ["replenishment", "late"] as const,
  negatives: ["replenishment", "negatives"] as const,
  reviewSafety: ["replenishment", "review", "safety-days"] as const,
  reviewLeadTimes: ["replenishment", "review", "lead-times"] as const,
  orderUnits: ["replenishment", "order-units"] as const,
};

/**
 * Round a quantity up to the bundle (a whole unit without one), then up to
 * the minimum order — ReplenishmentRules.orderable on the server, so what a
 * person types becomes what can be bought. Zero stays zero: not ordering is
 * always allowed.
 */
export function snapToBundle(quantity: number, multiple: number | null, minimum: number | null): number {
  if (!(quantity > 0)) return 0;
  const step = multiple && multiple > 0 ? multiple : 1;
  const up = (value: number) => Math.ceil(value / step - 1e-9) * step;
  let result = up(quantity);
  if (minimum && minimum > 0 && result < minimum) result = up(minimum);
  return Number(result.toFixed(4));
}

/** An order unit's name in the reader's language. */
export function orderUnitName(unit: { name?: string | null; name_fr?: string | null; code?: string | null }, language: string): string {
  const name = language.startsWith("fr") ? unit.name_fr || unit.name : unit.name;
  return name || unit.code || "";
}

/**
 * A stock quantity said in its PER_PART order unit: "2 × BARREL (416 L)".
 * Null when the line is not ordered in a PER_PART unit, so callers fall back
 * to the stock quantity alone.
 */
export function inOrderUnits(
  quantity: number,
  line: Pick<ReplenishmentLineDTO, "order_unit_kind" | "order_unit_code" | "stock_units_per_order_unit" | "stock_unit_code">,
  format: (value: number) => string,
): string | null {
  const per = Number(line.stock_units_per_order_unit ?? 0);
  if (line.order_unit_kind !== "PER_PART" || !(per > 0) || !line.order_unit_code) return null;
  const units = quantity / per;
  return `${format(units)} × ${line.order_unit_code} (${format(quantity)} ${line.stock_unit_code ?? ""})`.replace(" )", ")");
}

/** A fill fraction as a buyer says it: 1/4, 1/2, 3/4, full, else a percentage. */
export function fillLabel(fill: number | null | undefined, t: (key: string, fallback: string, options?: Record<string, unknown>) => string): string {
  if (fill == null) return "";
  if (Math.abs(fill - 1) < 1e-6) return t("order-unit.fill.full", "full");
  const known: Array<[number, string]> = [[0.25, "1/4"], [0.5, "1/2"], [0.75, "3/4"]];
  for (const [value, label] of known) if (Math.abs(fill - value) < 1e-6) return label;
  return `${String(Math.round(fill * 100))} %`;
}
